import { motion, type HTMLMotionProps } from "motion/react";
import type { ElementType, ReactNode } from "react";

const EASE = [0.22, 1, 0.36, 1] as const;

// Cache motion components per tag — creating them during render would remount
// the subtree on every parent update.
const cache = new Map<ElementType, ElementType>();
function motionFor(as: ElementType): ElementType {
  let component = cache.get(as);
  if (!component) {
    component = motion.create(as as never) as ElementType;
    cache.set(as, component);
  }
  return component;
}

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Vertical offset in px the element travels while fading in. */
  y?: number;
  delay?: number;
  duration?: number;
  once?: boolean;
  /** Render as a different element (section, article, li…). */
  as?: ElementType;
} & Omit<HTMLMotionProps<"div">, "children" | "initial" | "whileInView" | "viewport"> &
  Record<string, unknown>;

/** Fade + rise the element into view the first time it enters the viewport. */
export function Reveal({
  children,
  className,
  y = 24,
  delay = 0,
  duration = 0.6,
  once = true,
  as = "div",
  ...rest
}: RevealProps) {
  const Component = motionFor(as);
  return (
    <Component
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15, margin: "0px 0px -60px 0px" }}
      transition={{ duration, delay, ease: EASE }}
      className={className}
      {...(rest as object)}
    >
      {children}
    </Component>
  );
}

/** Parent that staggers the reveal of its <StaggerItem /> children. */
export function StaggerGroup({
  children,
  className,
  stagger = 0.08,
  as = "div",
  ...rest
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
  as?: ElementType;
} & Omit<HTMLMotionProps<"div">, "children"> &
  Record<string, unknown>) {
  const Component = motionFor(as);
  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger } },
      }}
      className={className}
      {...(rest as object)}
    >
      {children}
    </Component>
  );
}

export const staggerItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

/** Child of <StaggerGroup /> — also lifts slightly on hover. */
export function StaggerItem({
  children,
  className,
  hover = true,
  as = "div",
  ...rest
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  as?: ElementType;
} & Omit<HTMLMotionProps<"div">, "children"> &
  Record<string, unknown>) {
  const Component = motionFor(as);
  return (
    <Component
      variants={staggerItemVariants}
      whileHover={hover ? { y: -6 } : undefined}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      className={className}
      {...(rest as object)}
    >
      {children}
    </Component>
  );
}
